import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { WalletSetupStep } from './WalletSetupStep';
import { ONBOARDING_API, onboardingError } from './api';

export const WalletSetupReturn = ({ onComplete }) => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: Boolean(sessionId), card: null, error: '' });
  const [retry, setRetry] = useState(0);
  const [continuing, setContinuing] = useState(false);
  useEffect(() => {
    if (!sessionId) return undefined;
    let active = true, timer;
    let attempts = 0;
    setState({ loading: true, card: null, error: '' });
    const verify = async () => {
      try {
        const { data } = await axios.get(`${ONBOARDING_API}/wallet/status`, { params: { session_id: sessionId }, timeout: 15000 });
        if (!active) return;
        if (data.status === 'saved' && data.card) setState({ loading: false, card: data.card, error: '' });
        else if (data.status === 'pending' && ++attempts < 8) timer = setTimeout(verify, 1500);
        else setState({ loading: false, card: null, error: data.status === 'expired' ? 'This card setup link has expired. Start again or skip for now.' : 'Your card has not been confirmed yet. You can retry or skip for now.' });
      } catch (err) { if (active) setState({ loading: false, card: null, error: onboardingError(err, 'Could not verify card setup. Please retry.') }); }
    };
    verify();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId, retry]);
  const finish = async skip => {
    if (continuing) return;
    setContinuing(true);
    try { if (skip) await axios.post(`${ONBOARDING_API}/wallet/skip`); await onComplete(); }
    catch (err) { setState(previous => ({ ...previous, error: onboardingError(err, 'Could not continue. Please retry.') })); setContinuing(false); }
  };
  return <main className="min-h-[100dvh] bg-[var(--ps-bg-base)] text-[var(--ps-text-primary)] flex items-center justify-center px-4 py-8" data-testid="wallet-return-page">
    <div className="w-full max-w-lg space-y-5">
      {!sessionId ? <WalletSetupStep cancelled={params.get('cancelled') === '1'} onComplete={onComplete} /> : <>
        <h1 className="text-2xl sm:text-3xl font-bold" data-testid="wallet-return-title">{state.card ? 'Your card is ready' : 'Golf Wallet card setup'}</h1>
        {state.loading && <p role="status" className="flex items-center gap-2" data-testid="wallet-return-checking"><Loader2 className="animate-spin" size={20} />Confirming with Stripe…</p>}
        {state.card && <p role="status" className="flex gap-2" data-testid="wallet-return-card"><CheckCircle2 className="text-green-600 shrink-0" />{state.card.brand} ending in {state.card.last4} is saved. No payment was taken.</p>}
        {state.error && <p role="alert" className="text-sm text-red-600" data-testid="wallet-return-error">{state.error}</p>}
        {state.card ? <Button onClick={() => finish(false)} disabled={continuing} className="ps-on-color w-full bg-green-700 hover:bg-green-800 text-white" data-testid="wallet-return-continue">{continuing ? 'Continuing…' : 'Continue to intro'}</Button> : <div className="flex flex-wrap gap-2">
          {!state.loading && <Button disabled={continuing} onClick={() => setRetry(n => n + 1)} className="ps-on-color bg-green-700 hover:bg-green-800 text-white" data-testid="wallet-return-retry">Check again</Button>}
          <Button variant="outline" disabled={continuing} onClick={() => finish(true)} data-testid="wallet-return-skip">Skip for now</Button>
        </div>}
      </>}
    </div>
  </main>;
};